import React, { useEffect, useRef, useState } from "react";
import styles from "./TabSelectionHeader.module.scss";
import { Link } from "react-router-dom";
import TabSelection from "./tabSelection/TabSelection";
import { WorkSelection } from "./workSelection/WorkSelection";
import MajorFooter from "@features/common/footer/majorFooter/MajorFooter";
import Sidebar from "@features/common/sideBar/SideBar";
import {
  GIFTING_URL,
  GROUP_GIFT_URL,
  HAPPY_YOU_URL,
  PAGE_URLS,
  SOLUTIONS_HUB_URL,
} from "@constants/common";
import { HeaderRightCube, YggHeaderIcon } from "customIcons";

interface TabSelectionHeaderInterfce {
  children: any;
  disableStoreSelection?: boolean;
  isUserSignedIn?: boolean;
  isGuestUser?: boolean;
  isAtWorkPage?: boolean;
  listOpened?: boolean;
  currentComponentId?: string;
  handleWorkTabSelection?: () => void;
  currentActiveRegion?: any;
  isBasicHeader?: boolean;
}

interface TabInterface {
  label?: string;
  bgColor: string;
  link: string;
  fontColor?: string;
  bgImageUrl?: string;
  active?: boolean;
  content?: any;
}

/**
 * @method TabSelectionHeader
 * @description Header component with tab selection
 * @returns {React.ReactElement}
 */
const TabSelectionHeader = ({
  children,
  isUserSignedIn = false,
  isAtWorkPage,
  currentComponentId,
  handleWorkTabSelection,
  currentActiveRegion,
  isBasicHeader = false,
}: TabSelectionHeaderInterfce): React.ReactElement => {
  const [isOverlapping, setIsOverlapping] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // #. Tab data for each page
  const tabs: TabInterface[] = [
    {
      label: "Gifting",
      bgColor: "#FFD7E7",
      link: GIFTING_URL,
    },
    {
      label: "Group Gifting",
      bgColor: "#D2C9FF",
      link: GROUP_GIFT_URL,
    },
    {
      label: "Happy You Offers",
      bgColor: "#FF9B9B",
      link: HAPPY_YOU_URL,
    },
  ];

  useEffect(() => {
    const bgColor = "#99C6FF";
    document.documentElement.style.setProperty("--after-bg-color", bgColor);
  }, []);

  // #. Checks if header is overlapping with footer
  const checkOverlap = () => {
    const topEl = headerRef.current;
    const footerEl = footerRef.current;

    if (!topEl || !footerEl) return;

    const topRect = topEl.getBoundingClientRect();
    const footerRect = footerEl.getBoundingClientRect();

    const verticalBuffer = 20;
    const distance = footerRect.top - topRect.bottom;

    setIsOverlapping(distance <= verticalBuffer);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkOverlap);
    window.addEventListener("resize", checkOverlap);

    checkOverlap();

    return () => {
      window.removeEventListener("scroll", checkOverlap);
      window.removeEventListener("resize", checkOverlap);
    };
  }, []);

  // #. To hide/Unhide Header psuedo elements
  const [isHidden, setIsHidden] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHidden(entry.isIntersecting);
      },
      { threshold: 0.2 },
    );

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => {
      if (contentRef.current) {
        observer.unobserve(contentRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const solutionHubRect = contentRef.current.getBoundingClientRect();
      const scrollPosition = window.scrollY;
      const solutionHubTop = solutionHubRect.top + scrollPosition;

      if (scrollPosition >= solutionHubTop) {
        setIsHidden(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className={styles["tab-selection-wrapper"]}>
      <header
        id="tab-selection-header"
        ref={headerRef}
        className={
          isOverlapping || isHidden
            ? `${styles["tab-selection"]} ${styles["tab-selection--hide-pseudo"]}`
            : styles["tab-selection"]
        }
      >
        <div className={`${styles.wrapper} ${styles.header}`}>
          <div className={`${styles["header__left"]}`}>
            <Link to={PAGE_URLS.ISSUE_CREDITS} className={styles.logo}>
              <YggHeaderIcon />
            </Link>
          </div>
          <TabSelection tabs={tabs} />
          <div className={`${styles["header__right"]}`}>
            <WorkSelection
              isWorkTabActive={true}
              isUserSignedIn={isUserSignedIn}
              handleWorkTabSelection={handleWorkTabSelection}
            />
          </div>
        </div>
        {!isBasicHeader && (
          <Link
            to={SOLUTIONS_HUB_URL}
            className={styles["solutions-hub"]}
            target="_blank"
            rel="noopener noreferrer"
          >
            <HeaderRightCube />
          </Link>
        )}
        <div className={styles["tab-selection__spacer"]} />
      </header>

      {/* Tab Content */}
      <div
        style={{
          backgroundColor: "var(--after-bg-color)",
        }}
      >
        <div
          className={`${isAtWorkPage ? styles["tab-selection-atWork"] : ""} ${
            styles["tab-content"]
          }`}
        >
          <Sidebar />
          <div id="preview-wrap" className={styles["tab-content__children"]}>
            {children}
            <MajorFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabSelectionHeader;
